import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-client';

/**
 * Configuração do cache de servidor.
 *
 * ---
 *
 * NÃO REPETIR O QUE O SERVIDOR JÁ RECUSOU
 *
 * O padrão do React Query é três tentativas para qualquer erro. Aqui isso seria
 * errado em dois sentidos: um 422 ("fora do expediente") nunca vira 200 na
 * segunda tentativa, e um 401 dispararia três renovações de sessão seguidas.
 *
 * Só erro de rede e 5xx merecem insistência — e uma vez só, porque o cliente
 * está com o celular na mão esperando a tela.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (falhas, erro) => {
          if (!(erro instanceof ApiError)) return false;
          if (erro.status >= 400 && erro.status < 500) return false;
          return falhas < 1;
        },
        // Horário livre envelhece rápido: 30 segundos é o bastante para uma
        // navegação de ida e volta sem refazer a chamada, e curto o bastante
        // para não oferecer um horário que já foi tomado.
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Uma mutação repetida sozinha pode virar um segundo agendamento. Quem
        // decide tentar de novo é a pessoa, tocando no botão.
        retry: false,
      },
    },
  });
}

/** Chaves de cache, num lugar só — para invalidar sem caçar strings pelo app. */
export const queryKeys = {
  tenant: ['tenant'] as const,
  services: ['services'] as const,
  barbers: (serviceId?: string) => ['barbers', serviceId ?? 'todos'] as const,
  plans: ['plans'] as const,
  availability: (serviceId: string, date: string, barberId?: string) =>
    ['availability', serviceId, date, barberId ?? 'qualquer'] as const,
  appointments: (filtro: string) => ['appointments', filtro] as const,
  appointment: (id: string) => ['appointment', id] as const,
  mySubscription: ['me', 'subscription'] as const,
  myPlanBalance: ['me', 'plan'] as const,
  benefitPreview: (serviceId: string) => ['me', 'plan', 'preview', serviceId] as const,
  profile: ['me', 'profile'] as const,
  pixCharge: (id: string) => ['me', 'pix', id] as const,
};
